import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { CheckCircle, Clock, ArrowRight, DeviceMobile, ChatCircleText, EnvelopeSimple, WhatsappLogo, Wallet, AppleLogo, GoogleLogo, QrCode } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { api, formatUSD } from "../lib/api";
import { toast } from "sonner";
import { useT } from "../lib/i18n";

export default function CashRequestStatus() {
  const t = useT();
  const { cardId } = useParams();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id") || "";
  const autopay = sp.get("autopay") === "1";
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState(null);
  const [errStatus, setErrStatus] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  const [autopayCountdown, setAutopayCountdown] = useState(autopay ? 2 : null);

  // Magic handoff: when arriving via the receiver QR (?autopay=1), once the
  // card is active, count down 2s and redirect straight into the tap-to-pay
  // screen. Skipped when the sender is on this page themselves (no autopay
  // flag), so they still see the resend panel + handoff QR.
  useEffect(() => {
    if (!autopay || !card || card.status !== "active") return;
    if (autopayCountdown === null) return;
    if (autopayCountdown <= 0) {
      const last4 = (card.card_number || "").slice(-4);
      const exp = encodeURIComponent(card.expiry || "");
      navigate(`/pay?last4=${last4}&exp=${exp}`, { replace: true });
      return;
    }
    const t = setTimeout(() => setAutopayCountdown((n) => (n === null ? null : n - 1)), 1000);
    return () => clearTimeout(t);
  }, [autopay, autopayCountdown, card, navigate]);

  useEffect(() => {
    let alive = true;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/cash-request/${cardId}`, {
          params: sessionId ? { session_id: sessionId } : {},
        });
        if (!alive) return;
        setCard(r.data);
        setBusy(false);
        if (r.data.status === "active") {
          toast.success(t("cash.toast.funded"));
          return;
        }
        // Keep polling for up to ~3 minutes.
        if (attempts < 45) {
          setTimeout(tick, 4000);
        } else {
          // Stripe never confirmed within 3 minutes. Show the timeout state
          // so the user knows we stopped checking and gets a clear next step.
          setTimedOut(true);
        }
      } catch (e) {
        if (!alive) return;
        setErrStatus(e?.response?.status || 0);
        setErr(e?.response?.data?.detail || "Could not check status");
        setBusy(false);
      }
    };
    tick();
    return () => { alive = false; };
  }, [cardId, sessionId]);

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Header />
      <main className="mx-auto max-w-md px-6 py-12">
        {busy && !card && (
          <div className="text-center py-16" data-testid="cash-status-loading">
            <Clock size={48} weight="duotone" className="mx-auto text-[#4A5C50] animate-pulse" />
            <p className="serif text-2xl mt-4">{t("cash.confirming")}</p>
            <p className="text-sm text-[#4A5C50] mt-2 mono">{t("cash.confirming.sub")}</p>
          </div>
        )}
        {err && errStatus === 404 && (
          <div className="rounded-2xl border border-[#CBA35C]/40 bg-[#CBA35C]/10 p-6 text-[#8C6B21]" data-testid="cash-status-not-found">
            <p className="serif text-2xl text-[#112318]">{t("cash.notfound.title")}</p>
            <p className="text-sm mt-2 text-[#4A5C50]">
              {t("cash.notfound.body")}
            </p>
            <p className="text-[11px] mono mt-3 text-[#4A5C50] break-all">
              {t("cash.notfound.ref")} {cardId}
            </p>
            <div className="flex gap-2 mt-4">
              <Link to="/" className="inline-block">
                <Button className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white">
                  {t("cash.notfound.start")}
                </Button>
              </Link>
              <a href="mailto:support@zcardon.com" className="inline-block">
                <Button variant="outline" className="rounded-xl border-[#184A30]/30 text-[#184A30]">
                  {t("cash.notfound.contact")}
                </Button>
              </a>
            </div>
          </div>
        )}
        {err && errStatus !== 404 && (
          <div className="rounded-2xl border border-[#D96C4A]/40 bg-[#D96C4A]/10 p-6 text-[#B04E2F]" data-testid="cash-status-error">
            <p className="serif text-2xl">{t("cash.error.title")}</p>
            <p className="text-sm mt-2">{err}</p>
            <Link to="/" className="inline-block mt-4 text-sm underline">{t("cash.error.return")}</Link>
          </div>
        )}
        {card && card.status === "active" && (
          <div className="space-y-6" data-testid="cash-status-active">
            {autopay && autopayCountdown !== null && autopayCountdown >= 0 && (
              <div
                className="rounded-2xl border border-[#D96C4A]/40 bg-[#D96C4A]/10 p-5 text-center"
                data-testid="cash-status-autopay-banner"
              >
                <DeviceMobile size={28} weight="duotone" className="mx-auto text-[#D96C4A]" />
                <p className="serif text-xl text-[#B04E2F] mt-2">{t("cash.autopay.opening")}</p>
                <p className="text-xs text-[#4A5C50] mt-1">
                  {t("cash.autopay.redirect", { secs: autopayCountdown })}{" "}
                  <button
                    onClick={() => setAutopayCountdown(null)}
                    className="underline hover:no-underline"
                    data-testid="cash-status-autopay-cancel"
                  >
                    {t("cash.autopay.stay")}
                  </button>
                </p>
              </div>
            )}
            <div className="text-center">
              <CheckCircle size={56} weight="fill" className="mx-auto text-[#184A30]" />
              <p className="serif text-3xl mt-4">{t("cash.ready.title")}</p>
              <p className="text-sm text-[#4A5C50] mt-1">
                {t("cash.ready.subtitle", { amount: Number(card.amount_usd).toFixed(2), phone: card.receiver_phone })}
              </p>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-[#0A1F16] via-[#112318] to-[#1F4030] text-[#FBF9F6] p-6">
              <p className="text-[10px] tracking-[0.3em] uppercase opacity-70">{t("cash.card.label")}</p>
              <p className="serif text-2xl mono mt-2 tracking-[0.15em]" data-testid="cash-status-card-number">
                {card.card_number || "•••• •••• •••• ••••"}
              </p>
              <div className="flex gap-6 mt-4 text-sm">
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">{t("cash.card.exp")}</p>
                  <p className="mono mt-1">{card.expiry || "--/--"}</p>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">{t("cash.card.cvv")}</p>
                  <p className="mono mt-1">{card.cvv || "---"}</p>
                </div>
                <div className="ml-auto text-right">
                  <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">{t("cash.card.balance")}</p>
                  <p className="mono mt-1">{formatUSD(card.amount_usd)}</p>
                </div>
              </div>
            </div>
            <Link
              to={`/pay?last4=${(card.card_number || "").slice(-4)}&exp=${encodeURIComponent(card.expiry || "")}`}
              className="block"
              data-testid="cash-status-open-pay"
            >
              <Button className="w-full rounded-xl bg-[#D96C4A] hover:bg-[#B04E2F] text-white h-12">
                <DeviceMobile size={18} weight="duotone" className="mr-2" /> {t("cash.cta.open_pay")} <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <DeliveryStatusBanner delivery={card.delivery} receiverPhone={card.receiver_phone} t={t} />
            <HandoffQRPanel cardId={cardId} sessionId={sessionId} t={t} />
            <WalletPanel cardId={cardId} t={t} />
            <ResendPanel cardId={cardId} receiverPhone={card.receiver_phone} t={t} />
          </div>
        )}
        {card && card.status === "pending_payment" && !err && !timedOut && (
          <div className="rounded-2xl border border-[#CBA35C]/40 bg-[#CBA35C]/10 p-6 text-[#8C6B21]" data-testid="cash-status-pending">
            <Clock size={32} weight="duotone" className="text-[#CBA35C]" />
            <p className="serif text-xl mt-3">{t("cash.pending.title")}</p>
            <p className="text-sm mt-2">
              {t("cash.pending.body")}
            </p>
            <Link to="/" className="inline-block mt-4 text-sm underline">{t("cash.pending.back")}</Link>
          </div>
        )}
        {card && card.status === "pending_payment" && timedOut && (
          <div className="rounded-2xl border border-[#D96C4A]/40 bg-[#D96C4A]/10 p-6 text-[#B04E2F]" data-testid="cash-status-timeout">
            <p className="serif text-2xl">{t("cash.timeout.title")}</p>
            <p className="text-sm mt-2 text-[#4A5C50]">
              {t("cash.timeout.body", { phone: card.receiver_phone })}
            </p>
            <div className="flex gap-2 mt-4">
              <Link to="/" className="inline-block">
                <Button variant="outline" className="rounded-xl border-[#D96C4A] text-[#B04E2F]" data-testid="cash-timeout-restart">
                  {t("cash.timeout.start_over")}
                </Button>
              </Link>
              <Button
                onClick={() => window.location.reload()}
                className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white"
                data-testid="cash-timeout-retry"
              >
                {t("cash.timeout.try_again")}
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}



function ResendPanel({ cardId, receiverPhone, t }) {
  const [busy, setBusy] = useState(null);
  const [emailMode, setEmailMode] = useState(false);
  const [email, setEmail] = useState("");
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/public/delivery-config")
      .then((r) => { if (alive) setCfg(r.data); })
      .catch(() => { /* non-fatal */ });
    return () => { alive = false; };
  }, []);

  const send = async (channel, payload = {}) => {
    setBusy(channel);
    try {
      const r = await api.post(`/cash-request/${cardId}/resend`, { channel, ...payload });
      const sid = r?.data?.sid;
      if (channel === "email") setEmailMode(false);
      if (channel === "email" || !sid) {
        toast.success(channel === "email"
          ? "✓ Email sent — check inbox & spam folder"
          : `✓ Queued via ${channel}`,
          { duration: 6000 });
      } else {
        const tt = toast.loading(`Sending via ${channel}…`);
        let verdict = null;
        for (let i = 0; i < 6; i++) {
          await new Promise((res) => setTimeout(res, 2000));
          try {
            const s = await api.get(`/cash-request/${cardId}/resend-status/${sid}`);
            verdict = s.data;
            if (verdict.delivered || verdict.failed) break;
          } catch (e) {
            if (e?.response?.status === 404) break;
          }
        }
        toast.dismiss(tt);
        if (verdict?.failed) {
          toast.error(verdict.human || `Could not deliver via ${channel}`, { duration: 14000 });
        } else if (verdict?.delivered) {
          toast.success(verdict.human || `✓ Delivered via ${channel}`, { duration: 8000 });
        } else {
          toast.message(`Still pending — Twilio is retrying. We'll keep an eye on it.`, { duration: 8000 });
        }
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || `Could not send via ${channel}`;
      toast.error(detail, { duration: 12000 });
    } finally {
      setBusy(null);
    }
  };

  const emailSandbox = cfg?.email?.sandbox === true;
  const whatsappAvailable = cfg?.whatsapp?.configured === true;
  const smsDegraded = cfg?.sms?.toll_free_verified === false;

  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-4" data-testid="cash-resend-panel">
      <p className="text-xs tracking-[0.2em] uppercase text-[#4A5C50]">{t("cash.resend.title")}</p>
      {(smsDegraded || emailSandbox) && (
        <div
          className="mt-3 rounded-lg border border-[#CBA35C]/40 bg-[#CBA35C]/10 p-3 text-[11px] text-[#8C6B21] leading-relaxed"
          data-testid="cash-resend-degraded-banner"
        >
          <span className="font-semibold">{t("cash.resend.degraded.lead")}</span>
          {smsDegraded && (
            <> {t("cash.resend.degraded.sms")}</>
          )}
          {emailSandbox && (
            <> {t("cash.resend.degraded.email")}</>
          )}
          <span> {t("cash.resend.degraded.tail")}</span>
        </div>
      )}
      <div className="grid grid-cols-3 gap-2 mt-3">
        <Button
          variant="outline"
          disabled={!receiverPhone || busy === "sms"}
          onClick={() => send("sms")}
          className="rounded-xl flex flex-col h-auto py-3"
          data-testid="cash-resend-sms"
        >
          <ChatCircleText size={20} weight="duotone" />
          <span className="text-[11px] mt-1">{busy === "sms" ? t("cash.resend.sending") : t("cash.resend.text")}</span>
        </Button>
        <Button
          variant="outline"
          disabled={!receiverPhone || busy === "whatsapp" || !whatsappAvailable}
          onClick={() => send("whatsapp")}
          className="rounded-xl flex flex-col h-auto py-3 disabled:opacity-40"
          data-testid="cash-resend-whatsapp"
          title={whatsappAvailable ? "" : "WhatsApp not configured on this server"}
        >
          <WhatsappLogo size={20} weight="duotone" />
          <span className="text-[11px] mt-1">{busy === "whatsapp" ? t("cash.resend.sending") : t("cash.resend.whatsapp")}</span>
        </Button>
        <Button
          variant="outline"
          disabled={busy === "email"}
          onClick={() => setEmailMode(true)}
          className="rounded-xl flex flex-col h-auto py-3"
          data-testid="cash-resend-email"
        >
          <EnvelopeSimple size={20} weight="duotone" />
          <span className="text-[11px] mt-1">{t("cash.resend.email")}</span>
        </Button>
      </div>
      {emailMode && (
        <form
          onSubmit={(e) => { e.preventDefault(); if (email) send("email", { email }); }}
          className="mt-3 space-y-2"
          data-testid="cash-resend-email-form"
        >
          {emailSandbox && (
            <div
              className="rounded-lg border border-[#CBA35C]/40 bg-[#CBA35C]/10 p-2 text-[11px] text-[#8C6B21] leading-relaxed"
              data-testid="cash-resend-email-sandbox-notice"
            >
              {t("cash.resend.email_sandbox")}
            </div>
          )}
          <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">{t("cash.resend.email_label")}</Label>
          <input
            type="email"
            className="underline-input mono w-full"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            required
            data-testid="cash-resend-email-input"
          />
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={() => setEmailMode(false)} size="sm">
              {t("cash.resend.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={!email || busy === "email"}
              size="sm"
              className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white"
              data-testid="cash-resend-email-submit"
            >
              {busy === "email" ? t("cash.resend.sending") : t("cash.resend.email_send")}
            </Button>
          </div>
        </form>
      )}
      <p className="text-[10px] text-[#4A5C50] mt-3 mono">
        {t("cash.resend.limit", { phone: receiverPhone || t("cash.resend.no_phone") })}
      </p>
    </div>
  );
}


function WalletPanel({ cardId, t }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/wallet/config")
      .then((r) => { if (alive) setCfg(r.data); })
      .catch(() => { if (alive) setCfg({ apple_enabled: false, google_enabled: false }); });
    return () => { alive = false; };
  }, []);

  if (!cfg || (!cfg.apple_enabled && !cfg.google_enabled)) return null;

  const downloadApple = async () => {
    setBusy("apple");
    try {
      const r = await api.get(`/cash-request/${cardId}/wallet/apple`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.apple.pkpass" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `zcardon-${cardId.slice(0, 6)}.pkpass`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("cash.wallet.toast.apple"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("cash.wallet.toast.apple_failed"));
    } finally {
      setBusy(null);
    }
  };

  const openGoogle = async () => {
    setBusy("google");
    try {
      const r = await api.get(`/cash-request/${cardId}/wallet/google`);
      window.location.href = r.data.saveUrl;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("cash.wallet.toast.google_failed"));
      setBusy(null);
    }
  };

  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-4" data-testid="cash-wallet-panel">
      <div className="flex items-center gap-2 mb-3">
        <Wallet size={18} weight="duotone" className="text-[#184A30]" />
        <p className="text-xs tracking-[0.2em] uppercase text-[#4A5C50]">{t("cash.wallet.title")}</p>
      </div>
      <div className="grid gap-2">
        {cfg.apple_enabled && (
          <Button
            onClick={downloadApple}
            disabled={busy === "apple"}
            className="w-full rounded-xl bg-black hover:bg-neutral-800 text-white h-11"
            data-testid="cash-wallet-apple"
          >
            <AppleLogo size={20} weight="fill" className="mr-2" />
            {busy === "apple" ? t("cash.wallet.generating") : t("cash.wallet.apple")}
          </Button>
        )}
        {cfg.google_enabled && (
          <Button
            onClick={openGoogle}
            disabled={busy === "google"}
            className="w-full rounded-xl bg-[#1A73E8] hover:bg-[#1557B0] text-white h-11"
            data-testid="cash-wallet-google"
          >
            <GoogleLogo size={20} weight="bold" className="mr-2" />
            {busy === "google" ? t("cash.wallet.generating") : t("cash.wallet.google")}
          </Button>
        )}
      </div>
      <p className="text-[10px] text-[#4A5C50] mt-3 mono">
        {t("cash.wallet.note")}
      </p>
    </div>
  );
}


function DeliveryStatusBanner({ delivery, receiverPhone, t }) {
  if (!delivery) return null;
  const status = delivery.status;
  const errCode = delivery.error_code;
  const fallback = delivery.fallback_channel;

  if (!status || status === "delivered" || status === "sent" || status === "queued" || status === "accepted") {
    return null;
  }
  if (!["failed", "undelivered"].includes(status)) return null;

  const isToll = errCode === "30032" || errCode === "30007" || errCode === "30034";
  const fellBackOk = !!fallback;

  return (
    <div
      className={`rounded-xl border p-4 ${
        fellBackOk
          ? "bg-[#184A30]/5 border-[#184A30]/30 text-[#184A30]"
          : "bg-[#CBA35C]/10 border-[#CBA35C]/40 text-[#8C6B21]"
      }`}
      data-testid="cash-delivery-status-banner"
    >
      {fellBackOk ? (
        <>
          <p className="text-sm font-semibold">{t("cash.delivery.fallback_title", { fallback })}</p>
          <p className="text-xs mt-1 leading-relaxed">
            {t("cash.delivery.fallback_body", { fallback, phone: receiverPhone })}
          </p>
        </>
      ) : (
        <>
          <p className="text-sm font-semibold">
            {isToll ? t("cash.delivery.toll_blocked") : t("cash.delivery.failed_title")}
          </p>
          <p className="text-xs mt-1 leading-relaxed">
            {isToll ? t("cash.delivery.toll_body") : t("cash.delivery.failed_body")}{" "}
            <span className="font-semibold">{t("cash.delivery.cta_label")}</span>{" "}
            {t("cash.delivery.body_tail")}
          </p>
          {errCode && (
            <p className="text-[10px] mono mt-2 opacity-70">
              Twilio error {errCode}: {delivery.error_message || "Carrier rejection"}
            </p>
          )}
        </>
      )}
    </div>
  );
}


function HandoffQRPanel({ cardId, sessionId, t }) {
  const url = (typeof window !== "undefined" ? window.location.origin : "")
    + `/cash-request/${cardId}` + (sessionId ? `?session_id=${encodeURIComponent(sessionId)}&autopay=1` : `?autopay=1`);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t("cash.handoff.toast.copied"));
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error(t("cash.handoff.toast.copy_failed"));
    }
  };

  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5" data-testid="cash-handoff-qr-panel">
      <div className="flex items-start gap-2">
        <QrCode size={20} weight="duotone" className="text-[#184A30] shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-xs tracking-[0.2em] uppercase text-[#4A5C50]">{t("cash.handoff.eyebrow")}</p>
          <p className="text-sm text-[#112318] mt-1">
            {t("cash.handoff.body")}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-col items-center gap-3 sm:flex-row sm:items-start">
        <div className="rounded-xl bg-white p-3 border border-[#E5E0D8]" data-testid="cash-handoff-qr-svg">
          <QRCodeSVG value={url} size={148} fgColor="#0A1F16" bgColor="#FFFFFF" includeMargin={false} />
        </div>
        <div className="flex-1 w-full space-y-3">
          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase text-[#4A5C50]">{t("cash.handoff.share_label")}</Label>
            <div
              className="mt-1 break-all text-[11px] mono text-[#4A5C50] bg-[#FBF9F6] rounded-lg p-2 border border-[#E5E0D8]"
              data-testid="cash-handoff-qr-url"
            >
              {url}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={copy}
              variant="outline"
              className="rounded-xl"
              data-testid="cash-handoff-qr-copy"
            >
              {copied ? t("cash.handoff.copied") : t("cash.handoff.copy")}
            </Button>
            <Button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({ title: "Your Zcardon card", url }).catch(() => {});
                } else {
                  copy();
                }
              }}
              className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white"
              data-testid="cash-handoff-qr-share"
            >
              {t("cash.handoff.share")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
